import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Button,
  Card,
  CardBody,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  VStack,
  Text,
  Alert,
  AlertIcon,
  HStack,
} from '@chakra-ui/react'
import { Eye, EyeOff, Lock, CheckCircle } from 'lucide-react'
import { z } from 'zod'
import { useAuthStore } from '@/store/auth'
import { useThemeColors } from '@/hooks/useThemeColors'

const resetPasswordSchema = z
  .object({
    password: z.string().min(8, 'Password must be at least 8 characters'),
    password_confirmation: z.string().min(8, 'Password confirmation is required'),
    token: z.string().min(1, 'Reset token is required'),
  })
  .refine((data) => data.password === data.password_confirmation, {
    message: "Passwords don't match",
    path: ['password_confirmation'],
  })

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>

export function ResetPasswordForm() {
  const [isSuccess, setIsSuccess] = useState(false)
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [token, setToken] = useState('')
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { resetPassword, isLoading } = useAuthStore()
  const colors = useThemeColors()

  useEffect(() => {
    const tokenParam = searchParams.get('token')
    if (tokenParam) {
      setToken(tokenParam)
    } else {
      setError('Invalid or missing reset token')
    }
  }, [searchParams])

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      token,
    },
  })

  const onSubmit = async (data: ResetPasswordFormData) => {
    try {
      setError('')
      await resetPassword({
        token: data.token,
        password: data.password,
        password_confirmation: data.password_confirmation,
      })
      setIsSuccess(true)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reset password. Please try again.')
    }
  }

  if (isSuccess) {
    return (
      <Box minH="100vh" bg={colors.bg} display="flex" alignItems="center" justifyContent="center">
        <Card maxW="md" w="full" bg={colors.cardBg}>
          <CardBody p={8}>
            <VStack spacing={6}>
              <Box textAlign="center">
                <CheckCircle size={64} color="#48BB78" />
                <Heading size="lg" color="green.500" mb={2} mt={4}>
                  Password Reset Successfully
                </Heading>
                <Text color={colors.text}>
                  Your password has been reset successfully. You can now log in with your new password.
                </Text>
              </Box>

              <Button
                bg="blue.500"
                color="white"
                _hover={{ bg: 'blue.600' }}
                size="lg"
                w="full"
                onClick={() => navigate('/login')}
              >
                Go to Login
              </Button>
            </VStack>
          </CardBody>
        </Card>
      </Box>
    )
  }

  return (
    <Box minH="100vh" bg={colors.bg} display="flex" alignItems="center" justifyContent="center">
      <Card maxW="md" w="full" bg={colors.cardBg}>
        <CardBody p={8}>
          <VStack spacing={6}>
            <Box textAlign="center">
              <Heading size="lg" color="blue.500" mb={2}>
                Reset Your Password
              </Heading>
              <Text color={colors.text}>Enter your new password below.</Text>
            </Box>

            {error && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                {error}
              </Alert>
            )}

            <Box as="form" onSubmit={handleSubmit(onSubmit)} w="full">
              <VStack spacing={4}>
                <FormControl isInvalid={!!errors.password}>
                  <FormLabel>New Password</FormLabel>
                  <InputGroup>
                    <InputLeftElement>
                      <Lock size={20} />
                    </InputLeftElement>
                    <Input
                      {...register('password')}
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your new password"
                    />
                    <InputRightElement>
                      <Button variant="ghost" size="sm" onClick={() => setShowPassword(!showPassword)} p={0}>
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </Button>
                    </InputRightElement>
                  </InputGroup>
                  <FormErrorMessage>{errors.password?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.password_confirmation}>
                  <FormLabel>Confirm New Password</FormLabel>
                  <InputGroup>
                    <InputLeftElement>
                      <Lock size={20} />
                    </InputLeftElement>
                    <Input
                      {...register('password_confirmation')}
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Confirm your new password"
                    />
                    <InputRightElement>
                      <Button variant="ghost" size="sm" onClick={() => setShowConfirmPassword(!showConfirmPassword)} p={0}>
                        {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </Button>
                    </InputRightElement>
                  </InputGroup>
                  <FormErrorMessage>{errors.password_confirmation?.message}</FormErrorMessage>
                </FormControl>

                <Button
                  type="submit"
                  bg={colors.buttonColor}
                  color="white"
                  _hover={{ bg: 'blue.600' }}
                  size="lg"
                  w="full"
                  isLoading={isLoading}
                  loadingText="Resetting password..."
                  isDisabled={!token}
                >
                  Reset Password
                </Button>
              </VStack>
            </Box>

            <HStack justify="center" mt={4}>
              <Button variant="link" color={colors.link} fontSize="sm" onClick={() => navigate('/login')} _hover={{ textDecoration: 'underline' }}>
                Back to Login
              </Button>
            </HStack>
          </VStack>
        </CardBody>
      </Card>
    </Box>
  )
}

